import { useConnectionStore } from '@/stores/connection';
import { LoadingState } from '@/util/LoadingState';
import { API } from '@vp/common';
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { watchDebounced } from '@vueuse/core';

export const useArtistStore = defineStore('artist', () => {
  const connectionStore = useConnectionStore();
  const apiClient = connectionStore.apiClient!;

  const artistSearchFilter = ref<string>('');
  const searchedArtists = ref<API.Artist.SearchResultArtist.SearchResultArtist[]>([]);
  const artistSearchLoading = ref<LoadingState>(LoadingState.UNKNOWN);

  const artists = ref<Record<number, API.Artist.Artist.Artist>>({});
  const artistLoading = ref<Record<number, LoadingState>>({});
  const artistSaving = ref<LoadingState>(LoadingState.UNKNOWN);

  const updateSearchResults = async () => {
    try {
      artistSearchLoading.value = LoadingState.LOADING;

      searchedArtists.value = (await API.Artist.Api.searchArtists(apiClient, artistSearchFilter.value));
      artistSearchLoading.value = LoadingState.DONE;
    } catch (e) {
      artistSearchLoading.value = LoadingState.ERROR;
      throw e;
    }
  };

  watchDebounced(artistSearchFilter, () => {
    if (artistSearchFilter.value) {
      updateSearchResults();
    } else {
      searchedArtists.value = [];
    }
  }, { debounce: 500, maxWait: 1500 });

  const loadArtist = async (id: number) => {
    try {
      artistLoading.value[id] = LoadingState.LOADING;

      artists.value[id] = await API.Artist.Api.fetchArtist(apiClient, id);

      artistLoading.value[id] = LoadingState.DONE;
    } catch (e) {
      artistLoading.value[id] = LoadingState.ERROR;
      throw e;
    }
  };

  const saveArtist = async (artist: API.Artist.Artist.Artist) => {
    try {
      artistSaving.value = LoadingState.LOADING;

      await API.Artist.Api.saveArtist(apiClient, artist);

      artistSaving.value = LoadingState.DONE;
    } catch (e) {
      artistSaving.value = LoadingState.ERROR;
      throw e;
    }
  };

  return {
    searchedArtists,
    artistSearchFilter,
    artistSearchLoading,

    artists,
    artistLoading,
    artistSaving,

    updateSearchResults,
    loadArtist,
    saveArtist,
  };
});
